import React from 'react';
import { SchedulingDetailPanelProps } from './SchedulingContextTypes';
import {
  StageDetailContainer,
  StageDetailTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
} from './SchedulingContextStyles';

const DesignDetail: React.FC<{ details: Record<string, any> }> = ({ details }) => (
  <DetailGrid>
    <DetailItem>
      <DetailLabel>Design Status</DetailLabel>
      <DetailValue>{details.status || 'In Progress'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Designer</DetailLabel>
      <DetailValue>{details.designer || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Completion</DetailLabel>
      <DetailValue>{details.completion || '0'}%</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Due Date</DetailLabel>
      <DetailValue>{details.dueDate || 'N/A'}</DetailValue>
    </DetailItem>
  </DetailGrid>
);

const ManufacturingDetail: React.FC<{ details: Record<string, any> }> = ({ details }) => (
  <DetailGrid>
    <DetailItem>
      <DetailLabel>Production Line</DetailLabel>
      <DetailValue>{details.line || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Shift</DetailLabel>
      <DetailValue>{details.shift || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Operator</DetailLabel>
      <DetailValue>{details.operator || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Progress</DetailLabel>
      <DetailValue>{details.progress || '0'}%</DetailValue>
    </DetailItem>
  </DetailGrid>
);

const TestingDetail: React.FC<{ details: Record<string, any> }> = ({ details }) => (
  <DetailGrid>
    <DetailItem>
      <DetailLabel>Test Type</DetailLabel>
      <DetailValue>{details.testType || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Slot Time</DetailLabel>
      <DetailValue>{details.slotTime || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Test Status</DetailLabel>
      <DetailValue>{details.status || 'Pending'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Results</DetailLabel>
      <DetailValue>{details.results || 'In Progress'}</DetailValue>
    </DetailItem>
  </DetailGrid>
);

const LoadingDetail: React.FC<{ details: Record<string, any> }> = ({ details }) => (
  <DetailGrid>
    <DetailItem>
      <DetailLabel>Dock</DetailLabel>
      <DetailValue>{details.dock || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Container</DetailLabel>
      <DetailValue>{details.container || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Weight</DetailLabel>
      <DetailValue>{details.weight || 'N/A'}</DetailValue>
    </DetailItem>
    <DetailItem>
      <DetailLabel>Pallet Count</DetailLabel>
      <DetailValue>{details.palletCount || 'N/A'}</DetailValue>
    </DetailItem>
  </DetailGrid>
);

const DefaultDetail: React.FC<{ details: Record<string, any> }> = ({ details }) => (
  <DetailGrid>
    {Object.entries(details).map(([key, value]) => (
      <DetailItem key={key}>
        <DetailLabel>{key.replace(/([A-Z])/g, ' $1').trim()}</DetailLabel>
        <DetailValue>{String(value) || 'N/A'}</DetailValue>
      </DetailItem>
    ))}
  </DetailGrid>
);

export const SchedulingDetailPanel: React.FC<SchedulingDetailPanelProps> = ({
  currentStage,
  stageDetails,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <StageDetailContainer>
        <StageDetailTitle>Loading stage details...</StageDetailTitle>
      </StageDetailContainer>
    );
  }

  let DetailComponent;

  switch (currentStage) {
    case 'Design':
      DetailComponent = DesignDetail;
      break;
    case 'Manufacturing':
      DetailComponent = ManufacturingDetail;
      break;
    case 'Testing':
      DetailComponent = TestingDetail;
      break;
    case 'Loading':
      DetailComponent = LoadingDetail;
      break;
    default:
      DetailComponent = DefaultDetail;
  }

  return (
    <StageDetailContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <StageDetailTitle>Stage Details: {currentStage}</StageDetailTitle>
      <DetailComponent details={stageDetails} />
    </StageDetailContainer>
  );
};
